import { PersonalShell } from 'components/layout'
import paths from 'paths'
import React, { Suspense, useContext } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import {
  OrganizationAppIdResolver,
  UserAppIdResolver,
} from 'scenes/appManagement'
import { EmployeeGroupManagementRouter } from 'scenes/employeeGroupManagement'
import OrganizationManagementRouterRedirect from 'scenes/organizationManagement/OrganizationManagementRouterRedirect'
import UserAppsRouter from 'scenes/userManagement/tabs/apps/UserAppsRouter'
import UserManagementLayout from 'scenes/userManagement/UserManagementLayout'

import AppContext from './context'
import { useFeatureFlags } from './utils/featureFlags'

const IS_PRODUCTION = process.env.NODE_ENV === 'production'

// React Router re-renders a matched route in place when only params change,
// which leaves the resolvers holding stale data for the previous app. Keying
// by the identity params forces a remount (and fresh fetch) on switch.
const KeyedOrganizationAppIdResolver = () => {
  const { organizationSlug, appSlug } = useParams()
  return <OrganizationAppIdResolver key={`${organizationSlug}/${appSlug}`} />
}

const KeyedUserAppIdResolver = () => {
  const { userId, appSlug } = useParams()
  return <UserAppIdResolver key={`${userId ?? ''}/${appSlug}`} />
}

// Legacy `/app/:appSlug/*` URLs from before the app→database rename,
// preserving any deeper sub-path (e.g. `/app/foo/settings` →
// `/database/foo/settings`) as well as the query string and hash so deep
// links like `/app/foo/collections?dql=...` keep their params.
const LegacyAppRedirect = () => {
  const params = useParams<{ appSlug?: string; '*': string }>()
  const location = useLocation()
  const base = paths.app({ appSlug: params.appSlug ?? '' })
  const splat = params['*']
  const target =
    (splat ? `${base}/${splat}` : base) + location.search + location.hash
  return <Navigate to={target} replace />
}

/* -------------------------------------------------------------------------- */
/*                            App-specific Routes                             */
/* -------------------------------------------------------------------------- */

const DQLEditorScene = React.lazy(() => import('scenes/dqlEditor'))
const AppForgeEntry = React.lazy(() => import('scenes/forge'))

/* -------------------------------------------------------------------------- */
/*                            User Tenant Routes                              */
/* -------------------------------------------------------------------------- */

const CreateUserAppScene = React.lazy(
  () => import('scenes/userManagement/createUserApp'),
)
const FeedbackScene = React.lazy(() => import('scenes/feedback'))
const HomeScene = React.lazy(() => import('scenes/home'))
const InvitationScene = React.lazy(() => import('scenes/invitation'))
const NotificationsScene = React.lazy(
  () => import('scenes/userManagement/notifications'),
)
const UserOrganizationsScene = React.lazy(
  () =>
    import('scenes/userManagement/tabs/organizations/UserOrganizationsScene'),
)
const UserEmployeeGroupsScene = React.lazy(
  () =>
    import('scenes/userManagement/tabs/employeeGroups/UserEmployeeGroupsScene'),
)
const UserSettingsScene = React.lazy(
  () => import('scenes/userManagement/tabs/settings/UserSettingsScene'),
)

/* -------------------------------------------------------------------------- */
/*                        Organization Tenant Routes                          */
/* -------------------------------------------------------------------------- */

const CreateOrganizationScene = React.lazy(
  () => import('scenes/createOrganization'),
)
const CreateOrganizationAppScene = React.lazy(
  () => import('scenes/organizationManagement/CreateOrganizationAppScene'),
)
const OrganizationSettingsScene = React.lazy(
  () => import('scenes/organizationManagement/tabs/settings'),
)
const OrganizationActivity = React.lazy(
  () => import('scenes/organizationManagement/tabs/activity'),
)
const OrganizationApps = React.lazy(
  () => import('scenes/organizationManagement/tabs/apps'),
)
const OrganizationManagementScene = React.lazy(
  () => import('scenes/organizationManagement'),
)

/* -------------------------------------------------------------------------- */
/*                               Employee-only                                */
/* -------------------------------------------------------------------------- */

const CreateEmployeeGroupScene = React.lazy(
  () => import('scenes/createEmployeeGroup'),
)
const FindAppScene = React.lazy(() => import('scenes/findApp'))
const FindOrganizationScene = React.lazy(() => import('scenes/organizations'))
const FindEmployeeGroupScene = React.lazy(
  () => import('scenes/findEmployeeGroup'),
)
const UsersScene = React.lazy(() => import('scenes/users'))

const AppRouter = () => {
  const { isSuperAdmin } = useContext(AppContext)
  const { appForge } = useFeatureFlags()

  return (
    <Suspense fallback={null}>
      {/* Adding or renaming a static route segment below? Add it to
          RESERVED_ROUTE_NAMES in paths/reserved.ts so it can't be claimed
          as an organization slug. */}
      <Routes>
        <Route>
          <Route
            path="/users"
            element={
              isSuperAdmin ? (
                <PersonalShell breadcrumbs={[{ text: 'Users' }]}>
                  <UsersScene />
                </PersonalShell>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/find-database"
            element={
              isSuperAdmin ? (
                <PersonalShell breadcrumbs={[{ text: 'Find database' }]}>
                  <FindAppScene />
                </PersonalShell>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/find-organization"
            element={
              isSuperAdmin ? (
                <PersonalShell breadcrumbs={[{ text: 'Find organization' }]}>
                  <FindOrganizationScene />
                </PersonalShell>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {isSuperAdmin && (
            <Route
              path="/find-employee-group"
              element={
                <PersonalShell breadcrumbs={[{ text: 'Find employee group' }]}>
                  <FindEmployeeGroupScene />
                </PersonalShell>
              }
            />
          )}
          <Route
            path="/feedback"
            element={
              <PersonalShell breadcrumbs={[{ text: 'Feedback' }]}>
                <FeedbackScene />
              </PersonalShell>
            }
          />

          {/* TODO(forge): remove this production check */}
          {(!IS_PRODUCTION || !!appForge) && (
            <Route path="/forge/:databaseId/*" element={<AppForgeEntry />} />
          )}

          <Route path="/dql-editor/:appId" element={<DQLEditorScene />} />
          <Route
            path="/organizations/create"
            element={
              <PersonalShell
                breadcrumbs={[
                  { text: 'Organizations', to: '/organizations' },
                  { text: 'New organization' },
                ]}
              >
                <CreateOrganizationScene />
              </PersonalShell>
            }
          />
          {isSuperAdmin && (
            <Route
              path="/employee-groups/new"
              element={
                <PersonalShell
                  breadcrumbs={[
                    { text: 'Employee groups', to: '/employee-groups' },
                    { text: 'New employee group' },
                  ]}
                >
                  <CreateEmployeeGroupScene />
                </PersonalShell>
              }
            />
          )}
          {isSuperAdmin && (
            <Route
              path="/employee-groups/:employeeGroupSlug/*"
              element={<EmployeeGroupManagementRouter />}
            />
          )}
          <Route
            path="/invitation/:invitationId"
            element={
              <PersonalShell breadcrumbs={[{ text: 'Invitation' }]}>
                <InvitationScene />
              </PersonalShell>
            }
          />

          {/* Legacy redirects from the app→database rename. Static segments
              rank above the organization/app catch-alls below, so these take
              precedence for old bookmarks and deep links. */}
          <Route
            path="/find-app"
            element={<Navigate to={paths.findApp()} replace />}
          />
          <Route
            path="/new-app"
            element={<Navigate to={paths.createApp({})} replace />}
          />
          <Route path="/app/:appSlug/*" element={<LegacyAppRedirect />} />

          <Route
            path="/:organizationSlug"
            element={<OrganizationManagementScene />}
          >
            <Route
              path="new-database"
              element={<CreateOrganizationAppScene />}
            />
            {/* Legacy redirects from the app→database rename. */}
            <Route
              path="apps"
              element={
                <OrganizationManagementRouterRedirect area="databases" />
              }
            />
            <Route
              path="new-app"
              element={
                <OrganizationManagementRouterRedirect area="new-database" />
              }
            />
            <Route path="settings/*" element={<OrganizationSettingsScene />} />
            <Route
              path="access/*"
              element={<OrganizationSettingsScene section="access" />}
            />
            <Route path="databases" element={<OrganizationApps />} />
            <Route path="activity" element={<OrganizationActivity />} />
            <Route
              path=":appSlug/*"
              element={<KeyedOrganizationAppIdResolver />}
            />
            <Route path="" element={<Navigate to="settings" replace />} />
          </Route>
          <Route path="/login" element={<Navigate to="/" replace />} />

          <Route path="/" element={<HomeScene />} />

          <Route path="/*" element={<UserManagementLayout />}>
            <Route path="" element={<Navigate to="databases" replace />} />
            <Route path="databases/*" element={<UserAppsRouter />} />
            <Route path="new-database" element={<CreateUserAppScene />} />
            <Route path="organizations" element={<UserOrganizationsScene />} />
            {isSuperAdmin && (
              <Route
                path="employee-groups"
                element={<UserEmployeeGroupsScene />}
              />
            )}
            <Route path="settings" element={<UserSettingsScene />} />
            <Route path="notifications" element={<NotificationsScene />} />
          </Route>

          <Route
            path="/database/:appSlug/*"
            element={<KeyedUserAppIdResolver />}
          />
          <Route
            path="/u/:userId/:appSlug/*"
            element={<KeyedUserAppIdResolver />}
          />

          <Route path="/u/:userId/*" element={<UserManagementLayout />}>
            <Route path="" element={<Navigate to="databases" replace />} />
            <Route path="databases/*" element={<UserAppsRouter />} />
            <Route path="new-database" element={<CreateUserAppScene />} />
            <Route path="organizations" element={<UserOrganizationsScene />} />
            {isSuperAdmin && (
              <Route
                path="employee-groups"
                element={<UserEmployeeGroupsScene />}
              />
            )}
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}

export default AppRouter
